/**
 * Product analytics (O3).
 *
 * PostHog sits *behind* the existing `track()` seam: every call site that
 * already imports `{ track }` keeps working unchanged, and when PostHog is
 * initialized those events also flow to PostHog. The dev console.debug +
 * `t6:analytics` CustomEvent behavior is preserved verbatim so DevTools and any
 * test listeners are unaffected.
 *
 * No-op when `VITE_POSTHOG_KEY` is unset (local dev + CI never phone home):
 * `initAnalytics` does nothing, `track()` only logs/dispatches, and
 * `identifyUser`/`resetUser` are no-ops.
 *
 * Privacy posture (see docs/runbooks/data-layer.md):
 *  - PII minimization: identify only by the opaque Cognito `sub`; NEVER send
 *    email, raw search query text, or place names as event properties.
 *  - `person_profiles: 'identified_only'` so anonymous visitors aren't profiled.
 *  - EU residency: default `api_host` to PostHog EU cloud when unset.
 *  - Session replay is OFF (not enabled here) — extra PII surface, off by default.
 */

import posthog from 'posthog-js'
import { env } from '@/lib/env'

let initialized = false

/** Call once at app entry. No-op when VITE_POSTHOG_KEY is unset (local dev/CI). */
export function initAnalytics(): void {
  if (initialized || !env.VITE_POSTHOG_KEY) return
  posthog.init(env.VITE_POSTHOG_KEY, {
    // GDPR: EU residency by default; override via VITE_POSTHOG_HOST if needed.
    api_host: env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    capture_pageview: true,
    // Don't profile anonymous visitors (PII minimization).
    person_profiles: 'identified_only',
    // Session replay is OFF by default (extra PII surface — see runbook note).
    disable_session_recording: true,
  })
  initialized = true
}

/**
 * Stable funnel event names — the contract PostHog funnels/insights are built
 * on. NEVER rename without migrating the PostHog funnels/insights. Call sites
 * import from here so a name can't be typo'd.
 */
export const FunnelEvent = {
  AppLoaded: 'app_loaded',
  MapSearched: 'map_searched',
  AreaSelected: 'area_selected',
  TerrainPreviewRequested: 'terrain_preview_requested',
  JobStarted: 'job_started',
  JobCompleted: 'job_completed',
  DownloadClicked: 'download_clicked',
  UpgradePromptShown: 'upgrade_prompt_shown',
  UpgradePromptClicked: 'upgrade_prompt_clicked',
  // stripe-subscriptions funnel (C2). PostHog funnels get built after launch.
  // PricingViewed fires once on PricingPage mount; CheckoutStarted fires on a
  // subscribe CTA click with { tier, source } (source: 'pricing' here, 'register'
  // when C5 reuses it). CheckoutCompleted fires from C3's return handler when a
  // verify confirms the grant (pairs with CheckoutStarted → PostHog funnel).
  PricingViewed: 'pricing_viewed',
  CheckoutStarted: 'checkout_started',
  CheckoutCompleted: 'checkout_completed',
  // R6 build flow.
  BuildKindSelected: 'build_kind_selected',
  BuildStepViewed: 'build_step_viewed',
  BuildShapePresetSelected: 'build_shape_preset_selected',
  BuildSizeCommitted: 'build_size_committed',
  // Share cards (docs/tickets/share-cards S5). Properties are card ids,
  // formats, methods and timings only: never the model name, place or coords.
  ShareSheetOpened: 'share_sheet_opened',
  ShareCardViewed: 'share_card_viewed',
  ShareExportStarted: 'share_export_started',
  ShareExportCompleted: 'share_export_completed',
  ShareExportFailed: 'share_export_failed',
} as const

/** The build-flow version on R6 events (absent on pre-release events). */
export const FLOW_VERSION = 'v2'

export type FunnelEventName = (typeof FunnelEvent)[keyof typeof FunnelEvent]

interface AnalyticsEvent {
  name: string
  properties?: Record<string, unknown>
}

export function track(name: string, properties?: Record<string, unknown>): void {
  // Existing dev behavior (kept): observable in DevTools + drives test listeners.
  const event: AnalyticsEvent = { name, properties }
  console.debug('[t6:analytics]', event)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('t6:analytics', { detail: event }))
  }
  // PostHog capture only when initialized (key set). No-op otherwise.
  if (initialized) posthog.capture(name, properties)
}

/**
 * Identify by the Cognito `sub` (== the user_id the backend logs in O1) so
 * PostHog funnels join to backend telemetry on the same key. `tier` is a person
 * property used to slice funnels/retention by plan. No-op when uninitialized.
 */
export function identifyUser(userId: string, props?: { tier?: string }): void {
  if (initialized) posthog.identify(userId, props)
}

/** Reset PostHog's identity on logout. No-op when uninitialized. */
export function resetUser(): void {
  if (initialized) posthog.reset()
}
